import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../App';
import api from '../utils/api';
import LoadingState from '../components/LoadingState';

const Categories = () => {
    const navigate = useNavigate();
    const { showSuccess, showError } = useToast();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        slug: '',
        description: '',
        parent: 0,
        thumbnail_id: null,
        display: 'default',
    });

    useEffect(() => {
        loadCategories();
    }, []);

    const loadCategories = async () => {
        try {
            setLoading(true);
            const response = await api.getCategories();
            if (response.success) {
                setCategories(response.categories || []);
            }
        } catch (error) {
            showError('Failed to load categories');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            if (editingCategory) {
                const response = await api.updateCategory(editingCategory.id, formData);
                if (response.success) {
                    showSuccess('Category updated successfully');
                    loadCategories();
                    resetForm();
                }
            } else {
                const response = await api.createCategory(formData);
                if (response.success) {
                    showSuccess('Category created successfully');
                    loadCategories();
                    resetForm();
                }
            }
        } catch (error) {
            showError(error.message || 'Failed to save category');
        }
    };

    const handleEdit = (category) => {
        setEditingCategory(category);
        setFormData({
            name: category.name,
            slug: category.slug,
            description: category.description || '',
            parent: category.parent || 0,
            thumbnail_id: category.thumbnail_id || null,
            display: category.display || 'default',
        });
        setThumbnailPreview(category.thumbnail_url || null);
        setShowForm(true);
    };

    const handleDelete = async (categoryId) => {
        if (!confirm('Are you sure you want to delete this category?')) return;

        try {
            const response = await api.deleteCategory(categoryId);
            if (response.success) {
                showSuccess('Category deleted successfully');
                loadCategories();
            }
        } catch (error) {
            showError('Failed to delete category');
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            slug: '',
            description: '',
            parent: 0,
            thumbnail_id: null,
            display: 'default',
        });
        setEditingCategory(null);
        setShowForm(false);
        setThumbnailPreview(null);
    };

    // Build hierarchical category list with indentation
    const buildHierarchicalList = (categories, parentId = 0, level = 0) => {
        return categories
            .filter(cat => cat.parent === parentId)
            .map(cat => ({
                ...cat,
                level,
                children: buildHierarchicalList(categories, cat.id, level + 1)
            }));
    };

    const flattenHierarchy = (hierarchical) => {
        let flat = [];
        hierarchical.forEach(cat => {
            flat.push(cat);
            if (cat.children && cat.children.length > 0) {
                flat = flat.concat(flattenHierarchy(cat.children));
            }
        });
        return flat;
    };

    const hierarchicalCategories = buildHierarchicalList(categories);
    const flatCategories = flattenHierarchy(hierarchicalCategories);

    const [thumbnailPreview, setThumbnailPreview] = useState(null);

    const openMediaLibrary = () => {
        if (window.wp && window.wp.media) {
            const frame = window.wp.media({
                title: 'Select Category Image',
                button: { text: 'Use this image' },
                multiple: false
            });

            frame.on('select', function() {
                const attachment = frame.state().get('selection').first().toJSON();
                setFormData(prev => ({
                    ...prev,
                    thumbnail_id: attachment.id
                }));
                setThumbnailPreview(attachment.url);
            });

            frame.open();
        }
    };

    const removeThumbnail = () => {
        setFormData(prev => ({
            ...prev,
            thumbnail_id: null
        }));
        setThumbnailPreview(null);
    };

    // Don't show full screen loader, show inline instead

    return (
        <div className="page categories-page">
            <div className="page-container">
                <div className="page-header">
                    <div className="page-title">
                        <h1>Categories</h1>
                        <p>Manage product categories</p>
                    </div>
                    <div className="page-actions">
                        <button 
                            className="btn btn-primary"
                            onClick={() => setShowForm(!showForm)}
                        >
                            {showForm ? 'Cancel' : '+ Add Category'}
                        </button>
                    </div>
                </div>

                {showForm && (
                    <div className="taxonomy-form-section">
                        <h3>{editingCategory ? 'Edit Category' : 'Add New Category'}</h3>
                        <form onSubmit={handleSubmit}>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Name *</label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Slug</label>
                                    <input
                                        type="text"
                                        value={formData.slug}
                                        onChange={(e) => setFormData({...formData, slug: e.target.value})}
                                        placeholder="Auto-generated from name"
                                    />
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Description</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                                    rows="3"
                                />
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Parent Category</label>
                                    <select
                                        value={formData.parent}
                                        onChange={(e) => setFormData({...formData, parent: parseInt(e.target.value)})}
                                    >
                                        <option value="0">None</option>
                                        {flatCategories.filter(c => c.id !== editingCategory?.id).map(cat => (
                                            <option key={cat.id} value={cat.id}>
                                                {'—'.repeat(cat.level)} {cat.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Display Type</label>
                                    <select
                                        value={formData.display}
                                        onChange={(e) => setFormData({...formData, display: e.target.value})}
                                    >
                                        <option value="default">Default</option>
                                        <option value="products">Products</option>
                                        <option value="subcategories">Subcategories</option>
                                        <option value="both">Both</option>
                                    </select>
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Thumbnail</label>
                                {thumbnailPreview ? (
                                    <div className="thumbnail-preview">
                                        <img src={thumbnailPreview} alt="Category thumbnail" />
                                        <div className="thumbnail-actions">
                                            <button type="button" className="btn btn-secondary btn-sm" onClick={openMediaLibrary}>
                                                Change Image
                                            </button>
                                            <button type="button" className="btn btn-danger btn-sm" onClick={removeThumbnail}>
                                                Remove
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <button type="button" className="btn btn-secondary" onClick={openMediaLibrary}>
                                        Choose Image
                                    </button>
                                )}
                            </div>
                            <div className="form-actions">
                                <button type="submit" className="btn btn-primary">
                                    {editingCategory ? 'Update' : 'Create'} Category
                                </button>
                                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                <div className="taxonomy-list">
                    {loading ? (
                        <div style={{ padding: '40px', textAlign: 'center' }}>
                            <LoadingState message="Loading categories..." />
                        </div>
                    ) : categories.length === 0 ? (
                        <div className="empty-state">
                            <div className="empty-icon">🏷️</div>
                            <h3>No categories yet</h3>
                            <p>Create your first category to get started</p>
                        </div>
                    ) : (
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Slug</th>
                                    <th>Description</th>
                                    <th>Count</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {flatCategories.map(category => (
                                    <tr key={category.id}>
                                        <td>
                                            {category.thumbnail_url && (
                                                <img src={category.thumbnail_url} alt="" style={{width: '30px', height: '30px', marginRight: '10px', objectFit: 'cover', borderRadius: '4px'}} />
                                            )}
                                            <span style={{ marginLeft: `${category.level * 20}px` }}>
                                                {category.level > 0 && '— '}
                                                <strong>{category.name}</strong>
                                            </span>
                                        </td>
                                        <td>{category.slug}</td>
                                        <td>{category.description || '-'}</td>
                                        <td>{category.count}</td>
                                        <td>
                                            <button 
                                                className="btn btn-sm btn-secondary"
                                                onClick={() => handleEdit(category)}
                                            >
                                                Edit
                                            </button>
                                            <button 
                                                className="btn btn-sm btn-danger"
                                                onClick={() => handleDelete(category.id)}
                                                style={{marginLeft: '8px'}}
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Categories;
